/**
 * Lead Magnets — shared configuration
 *
 * Single source of truth for all gated downloadable assets. Used by:
 *  - InlineLeadMagnet (embedded inside markdown content)
 *  - ExitIntentPopup  (offers a magnet on exit based on page category)
 *
 * When adding a new magnet:
 *  1. Add an entry to LEAD_MAGNET_CONFIG
 *  2. Add its magnetKey → sequence mapping to LEAD_MAGNET_SEQUENCE_MAP in
 *     lib/nurture/pushmail.ts
 *  3. Add it to the appropriate resolver map below so exit-intent picks it up
 */

export interface LeadMagnetAsset {
  /** Display name of the asset */
  name: string;
  /** Short description shown in the CTA */
  description: string;
  /** URL revealed after email capture (e.g., GitHub repo) */
  url: string;
  /** Label for the revealed link button */
  linkLabel?: string;
}

export interface LeadMagnetConfig {
  headline: string;
  subtext: string;
  asset: LeadMagnetAsset;
}

export const LEAD_MAGNET_CONFIG: Record<string, LeadMagnetConfig> = {
  'windows-patching-toolkit': {
    headline: 'Stop Manually Forcing Updates',
    subtext: 'Get the production-ready PowerShell scripts referenced in this guide — force updates, reset components, generate compliance reports, and more.',
    asset: {
      name: 'Windows Patching Toolkit',
      description: '6 PowerShell scripts + troubleshooting guide',
      url: 'https://github.com/InventiveHQ/windows-patching-toolkit',
      linkLabel: 'Get the Toolkit on GitHub',
    },
  },
  'edr-decommission-toolkit': {
    headline: 'Migrating Endpoint Protection? Don\u2019t Wing It.',
    subtext: 'Get the step-by-step decommission checklist and PowerShell scripts for safely removing legacy EDR/AV agents.',
    asset: {
      name: 'EDR Decommission Toolkit',
      description: 'Checklist + 4 PowerShell scripts for safe agent removal',
      url: 'https://github.com/InventiveHQ/edr-decommission-toolkit',
      linkLabel: 'Get the Toolkit on GitHub',
    },
  },
  'crowdstrike-cheat-sheet': {
    headline: 'The CrowdStrike Falcon Admin Cheat Sheet',
    subtext: 'Quick-reference commands, pre-built exclusion templates for SQL Server, SCCM, Exchange, and Domain Controllers, plus sensor health check scripts.',
    asset: {
      name: 'CrowdStrike Falcon Cheat Sheet',
      description: 'Commands, exclusion templates, and health scripts',
      url: 'https://github.com/InventiveHQ/crowdstrike-falcon-cheat-sheet',
      linkLabel: 'Get the Cheat Sheet on GitHub',
    },
  },
  'claude-code-starter-kit': {
    headline: 'Claude Code Starter Kit',
    subtext: 'Drop-in CLAUDE.md templates for Next.js, Python, Go, Rust, and monorepos. Plus MCP server configs and a troubleshooting guide.',
    asset: {
      name: 'Claude Code Starter Kit',
      description: 'CLAUDE.md templates + MCP configs + troubleshooting',
      url: 'https://github.com/InventiveHQ/claude-code-starter-kit',
      linkLabel: 'Get the Starter Kit on GitHub',
    },
  },
  'm365-admin-toolkit': {
    headline: 'Microsoft 365 Admin PowerShell Toolkit',
    subtext: 'Production-ready scripts for Exchange Online, Teams, Intune, license reporting, Conditional Access export, and DNS verification.',
    asset: {
      name: 'M365 Admin Toolkit',
      description: '9 PowerShell scripts + quick reference guide',
      url: 'https://github.com/InventiveHQ/m365-admin-toolkit',
      linkLabel: 'Get the Toolkit on GitHub',
    },
  },
};

// ---------------------------------------------------------------------------
// Resolver — picks the best lead magnet to offer on exit-intent based on
// page context. Returns null when no reasonable match exists; callers should
// fall back to the generic "Get Expert Help" CTA in that case.
// ---------------------------------------------------------------------------

type PageType = 'kb_article' | 'tool_page' | 'blog_post';

/**
 * KB category → magnetKey.
 * Only categories where we have a genuinely relevant download are listed.
 * Unlisted categories fall through to the default (no magnet) CTA.
 */
const KB_CATEGORY_TO_MAGNET: Record<string, string> = {
  // AI dev tooling
  'claude': 'claude-code-starter-kit',
  'copilot': 'claude-code-starter-kit',
  'openai': 'claude-code-starter-kit',

  // Endpoint security
  'crowdstrike': 'crowdstrike-cheat-sheet',
  'defender': 'crowdstrike-cheat-sheet', // closest EDR-flavored magnet we have

  // M365 / IT admin
  'microsoft': 'm365-admin-toolkit',
  'microsoft-365': 'm365-admin-toolkit',
  'microsoft-azure': 'm365-admin-toolkit',
  'intune': 'm365-admin-toolkit',
  'google-workspace': 'm365-admin-toolkit',

  // Patching / update management
  // (no dedicated KB category for patching today, but kept here for clarity)
};

/**
 * Tool category → magnetKey.
 * Tools already convert well on their own — we only offer a magnet when
 * there's a clearly-relevant download (not for every category).
 */
const TOOL_CATEGORY_TO_MAGNET: Record<string, string> = {
  // (intentionally sparse — expand when new magnets exist)
};

/**
 * Blog slug keyword → magnetKey. Checked in order; first match wins.
 * Mirrors the spirit of BLOG_SEQUENCE_PATTERNS in pushmail.ts.
 */
const BLOG_MAGNET_PATTERNS: Array<{ pattern: RegExp; magnetKey: string }> = [
  { pattern: /patch|wsus|windows-update|usoclient|wuauclt/, magnetKey: 'windows-patching-toolkit' },
  { pattern: /edr-vs|edr-decommission|migrate-edr|replacing-edr/, magnetKey: 'edr-decommission-toolkit' },
  { pattern: /crowdstrike|falcon/, magnetKey: 'crowdstrike-cheat-sheet' },
  { pattern: /claude-code|claude-md|mcp-server/, magnetKey: 'claude-code-starter-kit' },
  { pattern: /m365|microsoft-365|entra|azure-ad|exchange-online/, magnetKey: 'm365-admin-toolkit' },
];

/**
 * Pick the best lead magnet for a page, or null if none fits.
 */
export function resolveLeadMagnetForPage(
  pageType: PageType,
  category: string | undefined,
  slug: string | undefined,
): { magnetKey: string; config: LeadMagnetConfig } | null {
  let magnetKey: string | null = null;

  if (pageType === 'kb_article' && category) {
    magnetKey = KB_CATEGORY_TO_MAGNET[category] ?? null;
  } else if (pageType === 'tool_page' && category) {
    magnetKey = TOOL_CATEGORY_TO_MAGNET[category] ?? null;
  } else if (pageType === 'blog_post' && slug) {
    for (const { pattern, magnetKey: key } of BLOG_MAGNET_PATTERNS) {
      if (pattern.test(slug)) {
        magnetKey = key;
        break;
      }
    }
  }

  if (!magnetKey) return null;
  const config = LEAD_MAGNET_CONFIG[magnetKey];
  if (!config) return null;
  return { magnetKey, config };
}
